import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, FileText, GraduationCap, Calendar, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [teilnehmerDialogOpen, setTeilnehmerDialogOpen] = useState(false);
  const [selectedKurs, setSelectedKurs] = useState(null);
  const [kursTeilnehmer, setKursTeilnehmer] = useState([]);
  const [loadingTeilnehmer, setLoadingTeilnehmer] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/teilnehmer/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform API response to match expected format
      const statsData = {
        ...response.data,
        total_teilnehmer: response.data.total_teilnehmer || 0,
        courses: (response.data.course_stats || []).map(stat => ({
          name: stat._id || 'Ohne Theoriekurs',
          count: stat.count || 0
        })),
        config: {
          online: response.data.system_online,
          modus: response.data.system_modus
        }
      };
      
      setStats(statsData);
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const fetchKursTeilnehmer = async (kursName) => {
    try {
      setLoadingTeilnehmer(true);
      setSelectedKurs(kursName);
      setTeilnehmerDialogOpen(true);
      
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/teilnehmer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter participants by this course
      // Handle "Ohne Theoriekurs" / null / "None" cases
      const teilnehmerDesKurses = response.data.filter(t => {
        const teilnehmerKurs = t.kurs || 'Ohne Theoriekurs';
        const suchKurs = kursName || 'Ohne Theoriekurs';
        
        // Match if both are null/None/undefined or if they match exactly
        if (suchKurs === 'Ohne Theoriekurs') {
          return !t.kurs || t.kurs === 'None' || t.kurs === 'null' || t.kurs === '';
        }
        return teilnehmerKurs === suchKurs;
      });
      setKursTeilnehmer(teilnehmerDesKurses);
    } catch (error) {
      console.error('Fehler beim Laden der Teilnehmer:', error);
      toast.error('Fehler beim Laden der Teilnehmer');
      setKursTeilnehmer([]);
    } finally {
      setLoadingTeilnehmer(false);
    }
  };

  const toggleSystemStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const newStatus = !stats.config.online;
      await axios.put(`${API}/admin/system/config`, 
        { online: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`System ${newStatus ? 'online' : 'offline'} gesetzt`);
      fetchStats();
    } catch (error) {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const changeModus = async (newModus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/system/config`, 
        { modus: newModus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Modus auf "${newModus}" gesetzt`);
      fetchStats();
    } catch (error) {
      toast.error('Fehler beim Ändern des Modus');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Übersicht</h1>
          <p className="text-gray-600 mt-1">Dashboard und Systemstatus</p>
        </div>
        
        {/* System Status Toggle */}
        {stats && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">System Status:</span>
            <Button
              onClick={toggleSystemStatus}
              variant="outline"
              size="sm"
              data-testid="system-status-toggle"
              className={`font-medium ${
                stats.config.online 
                  ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' 
                  : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
              }`}
            >
              {stats.config.online ? '● Online' : '● Offline'}
            </Button>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-gray-200 hover:shadow-md transition-shadow" data-testid="stat-teilnehmer">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Teilnehmer</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.total_teilnehmer || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow" data-testid="stat-kurse">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Kurse</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.courses?.length || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <GraduationCap className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow" data-testid="stat-textbausteine">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Textbausteine</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.textbausteine_count || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-orange-50 flex items-center justify-center">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modus Card */}
        <Card className="border border-gray-200 hover:shadow-md transition-shadow" data-testid="stat-modus">
          <CardContent className="p-6">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-3">Betriebsmodus</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => changeModus('Anmeldung')}
                  size="sm"
                  data-testid="modus-anmeldung-btn"
                  className={`flex-1 font-medium transition-all ${
                    stats?.config?.modus === 'Anmeldung'
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Anmeldung
                </Button>
                <Button
                  onClick={() => changeModus('Prüfung')}
                  size="sm"
                  data-testid="modus-pruefung-btn"
                  className={`flex-1 font-medium transition-all ${
                    stats?.config?.modus === 'Prüfung'
                      ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-sm'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Prüfung
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kurse Übersicht */}
      {stats?.courses && stats.courses.length > 0 && (
        <Card className="border border-gray-200" data-testid="kurse-overview">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-purple-600" />
              Kurse im Überblick
            </CardTitle>
            <CardDescription>Klicke auf einen Kurs um die Teilnehmer zu sehen</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.courses.map((course, idx) => (
                <Card 
                  key={idx} 
                  className="border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => fetchKursTeilnehmer(course.name)}
                >
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-semibold text-gray-900">
                        {course.name || 'Ohne Theoriekurs'}
                      </span>
                      <Badge className="bg-blue-600 text-white">
                        {course.count} TN
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leere State wenn keine Kurse */}
      {(!stats?.courses || stats.courses.length === 0) && (
        <Card className="border border-gray-200" data-testid="no-courses-card">
          <CardContent className="py-12 text-center">
            <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Kurse vorhanden</h3>
            <p className="text-gray-600 mb-4">Importiere Kurse über die Kursverwaltung</p>
            <Button 
              onClick={() => navigate('/admin/kursdetails')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="goto-kursdetails-btn"
            >
              Zur Kursverwaltung
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Teilnehmer Dialog */}
      <Dialog open={teilnehmerDialogOpen} onOpenChange={setTeilnehmerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Teilnehmer: {selectedKurs}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {loadingTeilnehmer ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : kursTeilnehmer.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>Keine Teilnehmer in diesem Kurs</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                <p className="text-sm text-gray-500 mb-3">
                  {kursTeilnehmer.length} Teilnehmer
                </p>
                {kursTeilnehmer.map((teilnehmer, index) => (
                  <div 
                    key={teilnehmer.id || index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                      {(teilnehmer.vorname?.[0] || '') + (teilnehmer.nachname?.[0] || '')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {teilnehmer.vorname} {teilnehmer.nachname}
                      </p>
                      {teilnehmer.email && (
                        <p className="text-xs text-gray-500 truncate">{teilnehmer.email}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setTeilnehmerDialogOpen(false)}>
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
